import {
  CaretDownOutlined,
  CaretUpOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import { GET_COMPANY_COUNT_BY_LOCATION } from '@frontend/data-access';
import { Card, Input, Select, Table } from 'antd';
import dayjs from 'dayjs';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo, useState } from 'react';

const { Option } = Select;

const PopularInsightsStats = () => {
  const { t } = useTranslation('admin');
  const [filterPeriod, setFilterPeriod] = useState('week');
  const [searchText, setSearchText] = useState('');
  const [data, setData] = useState<CompanyData[]>([]);
  const [prevData, setPrevData] = useState<CompanyData[]>([]);

  const [getData, { loading }] = useLazyQuery<{
    companyCountByLocation: CompanyData[];
  }>(GET_COMPANY_COUNT_BY_LOCATION, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getData, filterPeriod]);

  const getDateRange = (period: string, endDate: dayjs.Dayjs) => {
    let startDate = dayjs();

    switch (period) {
      case 'week':
        startDate = endDate.clone().subtract(7, 'day');
        break;
      case 'month':
        startDate = endDate.clone().subtract(1, 'month');
        break;
      case '3months':
        startDate = endDate.clone().subtract(3, 'month');
        break;
      case 'year':
        startDate = endDate.clone().subtract(1, 'year');
        break;
    }

    return {
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
    };
  };

  const fetchData = async () => {
    const currentEndDate = dayjs();
    const { startDate, endDate } = getDateRange(filterPeriod, currentEndDate);

    await getData({
      variables: { startDate, endDate },
    }).then(({ data }) => {
      setData(data?.companyCountByLocation);
    });

    // Get previous period data
    const prevEndDate = dayjs(startDate);
    const { startDate: startDate1, endDate: endDate1 } = getDateRange(
      filterPeriod,
      prevEndDate
    );

    await getData({
      variables: { startDate: startDate1, endDate: endDate1 },
    }).then(({ data }) => {
      setPrevData(data?.companyCountByLocation);
    });
  };

  const getPercentChange = (value: number, prevValue: number): number => {
    if (prevValue === 0 && value === 0) {
      return 0;
    }
    if (prevValue === 0) {
      return 100;
    }
    return ((value - prevValue) / prevValue) * 100;
  };

  const stats = useMemo(() => {
    return data
      .filter((item) => item.location)
      .map((item) => {
        const prevItem = prevData.find(
          (prevItem) => prevItem.location === item.location
        );
        return {
          country: item.location,
          companies: item.count,
          change: getPercentChange(item.count, prevItem?.count || 0),
        };
      });
  }, [data, prevData]);

  const filteredStats = useMemo(() => {
    return stats.filter((item) =>
      item.country.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [stats, searchText]);

  const columns = [
    {
      title: t('country'),
      dataIndex: 'country',
      key: 'country',
      width: '60%',
    },
    {
      title: t('companies'),
      dataIndex: 'companies',
      key: 'companies',
      width: '20%',
    },
    {
      title: t('percent_change'),
      dataIndex: 'change',
      key: 'change',
      width: '20%',
      render: (change: number) => {
        const isPositive = change >= 0;
        return (
          <span
            className={`inline-block px-2 py-1 rounded-full font-medium text-sm ${
              isPositive
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            }`}
          >
            {isPositive ? <CaretUpOutlined /> : <CaretDownOutlined />}{' '}
            {Math.round(change)}%
          </span>
        );
      },
    },
  ];

  const handleFilterChange = (key: string) => {
    setFilterPeriod(key);
  };

  return (
    <Card style={{ borderRadius: 20 }}>
      <div className="space-y-6">
        <div className="flex flex-col space-y-4">
          <span className="text-sm text-gray-900 font-medium">
            {t('company_growth_by_country')}
          </span>
          <div className="flex space-x-4">
            <Input
              placeholder={t('country_name')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="flex-grow"
              style={{ height: '40px' }}
              suffix={<SearchOutlined style={{ color: 'rgba(0,0,0,.45)' }} />}
            />
            <Select value={filterPeriod} onChange={handleFilterChange}>
              <Option value="week">{t('last_7_days')}</Option>
              <Option value="month">{t('last_30_days')}</Option>
              <Option value="3months">{t('last_3_months')}</Option>
              <Option value="year">{t('last_year')}</Option>
            </Select>
          </div>
        </div>
        <Table
          columns={columns}
          dataSource={filteredStats}
          loading={loading}
          pagination={false}
          rowKey="country"
        />
      </div>
    </Card>
  );
};

interface CompanyData {
  location?: string;
  count: number;
  all: number;
}

export default PopularInsightsStats;
